import React, { useState } from "react";
import styled from "styled-components";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";

import SubmitButton from "../components/Buttons/SubmitButton";
import Hero from "../components/HeroSection/Hero";
import useTiposIdentificacion from "../hooks/parameter/useTiposIdentificacion";
import CustomSelect from "../components/Inputs/CustomSelect";
import CustomInput from "../components/Inputs/CustomInput";
import { getPqrs } from "../services/pqr.service";
import useConfigs from "../hooks/useConfigs";
import useThemes from "../hooks/useThemes";

const schema = yup.object({
  tipoDocumento: yup.string().required("El tipo de documento es requerido"),
  DocNumero: yup
    .number()
    .typeError("Ingrese un dato válido")
    .integer("Ingrese solo números enteros")
    .positive("El número de documento debe ser positivo")
    .required("El número de documento es requerido"),
  numeroRadicado: yup.string(),
});

const Preguntas = () => {
  const { tiposIdentificacion } = useTiposIdentificacion();
  const { fontSizeMultiplier } = useConfigs();
  const {
    backgroundWhite,
    backgroundCuarto,
    colorPrincipal,
    colorSecundario,
    colorCuarto,
  } = useThemes();

  const { handleSubmit, control } = useForm({
    mode: "onBlur",
    resolver: yupResolver(schema),
  });

  const [pqrs, setPqrs] = useState([]);
  const [itemSelected, setItemSelected] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isEmpty, setIsEmpty] = useState(false);

  const onSubmit = async (data) => {
    setIsLoading(true);

    try {
      const response = await getPqrs({
        Radicado: data.numeroRadicado,
        docUsuario: data.DocNumero,
      });

      setPqrs(response.data.list.reverse());
      setIsLoading(false);

      if (response.data.list.length === 0) {
        setIsEmpty(true);
      } else {
        setIsEmpty(false);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleClickSelected = (item) => {
    setItemSelected((prevState) => (prevState === item.id ? null : item.id));
  };

  return (
    <Container background={backgroundWhite}>
      <Hero
        title="Consulta de PQRs"
        subtitle="resuelve tus preguntas, quejas y reclamos"
        color={backgroundWhite}
        imgSrc="img/Servicios/consultas-quejas-reclamos.jpg"
      />

      <FormContainer
        onSubmit={handleSubmit(onSubmit)}
        background={backgroundWhite}
        color={colorPrincipal}
      >
        <InputContainer>
          <Controller
            name="tipoDocumento"
            control={control}
            render={({
              field: { value, onBlur, onChange },
              fieldState: { error },
            }) => (
              <CustomSelect
                value={value}
                onChange={onChange}
                onBlut={onBlur}
                label="Tipo de documento *"
                width="49%"
                error={error}
                data={tiposIdentificacion.map((item) => {
                  return { name: item.docName, value: JSON.stringify(item) };
                })}
              />
            )}
          />

          <Controller
            name="DocNumero"
            control={control}
            render={({
              field: { value, onBlur, onChange },
              fieldState: { error },
            }) => (
              <CustomInput
                value={value}
                onChange={onChange}
                onBlut={onBlur}
                label="Número de documento *"
                width="49%"
                error={error}
                type="number"
              />
            )}
          />
        </InputContainer>

        <InputContainer>
          <Controller
            name="numeroRadicado"
            control={control}
            render={({
              field: { value, onBlur, onChange },
              fieldState: { error },
            }) => (
              <CustomInput
                value={value}
                onChange={onChange}
                onBlut={onBlur}
                label="Número de radicado *"
                error={error}
              />
            )}
          />
        </InputContainer>
        <SubmitButton label="Buscar" />
        <hr />
      </FormContainer>
      {isLoading ? (
        <div className="spinner">
          <Spinner
            animation="grow"
            style={{ marginRight: "1rem" }}
            variant="secondary"
          />
          <Spinner
            animation="grow"
            style={{ marginRight: "1rem" }}
            variant="secondary"
          />
          <Spinner
            animation="grow"
            // style={{ marginRight: "1rem" }}
            variant="secondary"
          />
        </div>
      ) : (
        <InfoContainer
          fontsizemultiplier={fontSizeMultiplier}
          background={backgroundWhite}
          itembackground={backgroundCuarto}
          colortipografia={colorCuarto}
        >
          {isEmpty ? (
            <div className="alert">La búsqueda no arrojo resultado</div>
          ) : (
            pqrs.map((item) => (
              <div className="item-container" key={item.id}>
                <Item
                  onClick={() => handleClickSelected(item)}
                  item={item}
                  itemselected={itemSelected}
                  fontSizeMultiplier={fontSizeMultiplier}
                  itembackground={backgroundCuarto}
                  datecolor={colorSecundario}
                  color={colorPrincipal}
                >
                  <p className="fecha">{item.fechahora.split(" ")[0]}</p>
                  <p className="asunto">{item.asunto}</p>
                  <FontAwesomeIcon
                    className="icono"
                    icon={
                      item.id === itemSelected ? faChevronUp : faChevronDown
                    }
                    color={colorPrincipal}
                  />
                </Item>

                {itemSelected === item.id && (
                  <div className="comentario">{item.comentario}</div>
                )}
              </div>
            ))
          )}
        </InfoContainer>
      )}
    </Container>
  );
};

export default Preguntas;

const Container = styled.div`
  position: relative;

  .spinner {
    display: flex;
    justify-content: center;
    padding: 2rem;
    background-color: ${(props) => props.background};
  }
`;

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: ${(props) => props.background};
  margin-top: 5vh;
  margin-bottom: 0;

  hr {
    width: 50vw;
    opacity: 0.5;
    background-color: ${(props) => props.color};
  }

  @media (max-width: 1023px) {
    hr {
      width: 90vw;
    }
  }
`;

const InputContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 50vw;

  @media (max-width: 1023px) {
    flex-wrap: wrap;
    width: 90vw;
  }
`;

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding-bottom: 2rem;
  background-color: ${(props) => props.background};

  .alert {
    width: 50vw;
    border-radius: 2rem;
    text-align: center;
    border: 1px solid #ff7777;
    color: #ff7777;

    @media (max-width: 1023px) {
      width: 90vw;
    }
  }

  .item-container {
    width: 50vw;

    @media (max-width: 1023px) {
      width: 90vw;
    }
  }

  .comentario {
    padding: 1rem 2rem;
    margin-bottom: 1rem;
    font-size: ${(props) => 14 * props.fontsizemultiplier};
    font-weight: 700;
    background-color: ${(props) => props.itembackground};
    color: ${(props) => props.colortipografia};

    @media (max-width: 1023px) {
      padding: 0.5rem 1rem;
    }
  }
`;

const Item = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 50vw;
  margin-bottom: ${(props) =>
    props.item.id === props.itemselected ? "0.2rem" : "1rem"};
  padding: 0;
  background-color: ${(props) => props.itembackground};
  cursor: pointer;

  .fecha {
    width: 20%;
    margin-bottom: 0;
    padding: 1rem 2rem;
    font-size: ${(props) => 14 * props.fontsizemultiplier};
    font-weight: 700;
    color: ${(props) => props.datecolor};

    @media (max-width: 1023px) {
      width: 34%;
      padding: 0.5rem 1rem;
    }
  }

  .asunto {
    width: 72%;
    padding: 1rem 2rem;
    margin-bottom: 0;
    font-size: ${(props) => 14 * props.fontsizemultiplier};
    font-weight: 700;
    border-right: 1px solid ${(props) => props.datecolor};
    color: ${(props) => props.color};

    @media (max-width: 1023px) {
      width: 58%;
      padding: 0.5rem 1rem;
    }
  }

  .icono {
    width: 8%;
    margin-bottom: 0;
  }

  @media (max-width: 1023px) {
    width: 90vw;
  }
`;
